/**
 * Per-structure visual-effect contract.
 *
 * Pool sizes are the actual allocated instance counts ImpactFx mounts.
 * `off` remains a true zero-resource path so evaluation can compare physics
 * with presentation disabled.
 *
 * CPU dust is a mobile-capped fallback (256). High-tier 4× CPU dust and the
 * GPU linger split are gone. Overlay still routes dust-bearing FX methods
 * into this pool; particle fines/grain capacities on Demolition/CottageTown
 * are 0 so these cards remain the airborne look.
 */

export type DemolitionEffectTier = 'off' | 'basic' | 'high';

export interface DemolitionVisualState {
  readonly tier: DemolitionEffectTier;
  readonly mounted: boolean;
  /** Sum of debris + dust + streak instance slots actually allocated. */
  readonly particlePoolSize: number;
  readonly debrisPoolSize: number;
  readonly dustPoolSize: number;
  readonly streakPoolSize: number;
  readonly shockwavePoolSize: number;
  readonly burstPoolSize: number;
  readonly damageStampPoolSize: number;
  readonly decalPoolSize: number;
  readonly rendererPasses: number;
  readonly audioNodes: number;
}

const VALID_TIERS: readonly DemolitionEffectTier[] = ['off', 'basic', 'high'];

const BASIC_DEBRIS = 128;
const BASIC_DUST = 256;
const BASIC_STREAK = 16;
const BASIC_SHOCKWAVE = 12;
const BASIC_BURST = 16;
const BASIC_STAMPS = 256;
const BASIC_DECALS = 256;

/** Mass-scale anchors for demolitionParticleMassScale (basic 4096 / high 16384 = 0.25). */
export const GPU_DUST_HIGH_CAPACITY = 16384;
export const GPU_DUST_BASIC_CAPACITY = 4096;

export function gpuDustCapacityForTier(tier: DemolitionEffectTier): number {
  if (tier === 'high') return GPU_DUST_HIGH_CAPACITY;
  if (tier === 'basic') return GPU_DUST_BASIC_CAPACITY;
  return 0;
}

export function resolveDemolitionEffectTier(
  search: string | URLSearchParams =
    typeof window === 'undefined' ? '' : window.location.search,
): DemolitionEffectTier {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  const requested = params.get('destructionFx');
  return requested && VALID_TIERS.includes(requested as DemolitionEffectTier)
    ? (requested as DemolitionEffectTier)
    : 'basic';
}

export function createDemolitionVisualState(tier: DemolitionEffectTier): DemolitionVisualState {
  if (tier === 'off') {
    return Object.freeze({
      tier,
      mounted: false,
      particlePoolSize: 0,
      debrisPoolSize: 0,
      dustPoolSize: 0,
      streakPoolSize: 0,
      shockwavePoolSize: 0,
      burstPoolSize: 0,
      damageStampPoolSize: 0,
      decalPoolSize: 0,
      rendererPasses: 0,
      audioNodes: 0,
    });
  }

  const high = tier === 'high';
  const debrisPoolSize = high ? BASIC_DEBRIS * 3 : BASIC_DEBRIS;
  // Mobile cap for every mounted tier. High-tier 4× CPU dust died with gpu-dust.
  const dustPoolSize = BASIC_DUST;
  const streakPoolSize = high ? BASIC_STREAK * 3 : BASIC_STREAK;
  const shockwavePoolSize = BASIC_SHOCKWAVE;
  const burstPoolSize = BASIC_BURST;

  return Object.freeze({
    tier,
    mounted: true,
    particlePoolSize: debrisPoolSize + dustPoolSize + streakPoolSize,
    debrisPoolSize,
    dustPoolSize,
    streakPoolSize,
    shockwavePoolSize,
    burstPoolSize,
    damageStampPoolSize: BASIC_STAMPS,
    decalPoolSize: BASIC_DECALS,
    // Main color pass + scene-depth pre-pass for soft particles.
    rendererPasses: 2,
    audioNodes: 0,
  });
}

export function tierDustMultiplier(tier: DemolitionEffectTier): number {
  return tier === 'off' ? 0 : 1;
}

export function tierDebrisMultiplier(tier: DemolitionEffectTier): number {
  return tier === 'high' ? 3 : tier === 'basic' ? 1 : 0;
}
